/**
 * Canonical photo gallery + lightbox panel for ToolSight.
 *
 * Supports two modes:
 *
 *   - Fetch mode: pass {toolId, callerEmail}. Component fetches
 *     via getMedia and renders the result. Used by recon hubs
 *     and tool-detail screens.
 *
 *   - Direct mode: pass {photoUrls: string[]} of pre-signed SAS
 *     URLs. Component skips the fetch and renders directly.
 *     Used by disposition admin where photos are pre-embedded
 *     in the parent endpoint response.
 *
 * Mode selection: if photoUrls is provided, direct mode wins
 * regardless of toolId. If neither is provided, renders empty
 * state. Dev-mode console.warn fires for invalid prop combos.
 *
 * Reference-stability contract: photoUrls must be a stable
 * reference across renders (memoize at the parent or pass a
 * field directly from a memoized object). The cleanup effect
 * watches photoUrls in its deps array; passing a fresh array
 * literal on every render will cause the lightbox to refuse to
 * stay open. The current consumer (DispositionDetailPane) reads
 * tool.photo_urls from a useMemo'd selectedTool, which is
 * stable across renders.
 *
 * Layout:
 *   - layout='hero' (default): one large hero image (max
 *     480×360) plus a thumbs row. Used by recon hubs.
 *   - layout='grid': uniform 96×96 grid, no hero. Used by
 *     disposition admin where information density matters.
 *
 * Resolved Phase 6a/6b decisions:
 *   - Self-fetches via getMedia(toolId, callerEmail) on mount and toolId change.
 *   - Owns its own loading / error / empty / populated states.
 *   - Owns its own lightbox state and replace-only history navigation.
 *   - hashSentinel prop (default 'photo') makes multi-instance use on a single
 *     route possible without URL collisions. Hash is rendered as `#${sentinel}`.
 *   - Renders panel content + lightbox only. Consumers must wrap in their
 *     own collapsible section (e.g. SortableSection) — this component does
 *     NOT include a section title, drag handle, or expand/collapse chrome.
 *
 * Replace-semantics rationale: the lightbox URL hash is a state sentinel
 * only; both open and close are navigate(..., { replace: true }) so the
 * back-stack is never modified, and browser-back from an open lightbox
 * naturally returns to the prior real entry while a location.key effect
 * silently closes the lightbox. The Phase 2c push/pop alternative was
 * abandoned because pop semantics assume what's at history position N-1,
 * and stale entries below the lightbox push silently rewrote page state.
 *
 * X-bubble bug latent: the X button (line ~152) sits inside the backdrop
 * div with the same handler. The double-fire is currently idempotent
 * because we never call window.history.back() — replace-only navigation
 * makes the second fire a no-op. If anyone reintroduces pushState here
 * (e.g., for a future close-in-place attempt), the X button must be
 * wrapped in onClick={(e) => { e.stopPropagation(); handleUserClose(); }}
 * to avoid the same bug pattern that bit ToolDetailScreen.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMedia } from '../services/mediaApi';
import './PhotoGalleryPanel.css';

export default function PhotoGalleryPanel({
  toolId,
  callerEmail,
  photoUrls,
  hashSentinel = 'photo',
  layout = 'hero',
}) {
  if (process.env.NODE_ENV !== 'production') {
    const fetchMode  = toolId != null;
    const directMode = photoUrls != null;
    if (fetchMode && directMode) {
      // eslint-disable-next-line no-console
      console.warn(
        'PhotoGalleryPanel: received both toolId and photoUrls. ' +
        'Direct-mode (photoUrls) takes precedence; toolId+callerEmail ' +
        'will be ignored. Pass exactly one mode per consumer.'
      );
    }
  }

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const sentinel = `#${hashSentinel}`;

  /* ── Fetch on mount and toolId change ────────────────────────────── */
  useEffect(() => {
    // Phase B (lightbox sprint): defensive guard. Clears lightbox state
    // and drops the URL hash if toolId changes. The current UX makes
    // this case structurally unreachable — the lightbox is a full-
    // screen modal with a click-anywhere-closes backdrop, so a user
    // cannot click a different tool row without the lightbox closing
    // first. Kept as cheap insurance against future redesigns where
    // the lightbox may not cover the full screen (e.g., a corner-
    // floating viewer or PiP mode), or any programmatic toolId change
    // path that may emerge later. +35 B gzipped, idempotent under the
    // current replace-only navigation pattern. See session-closing doc
    // for the audit-methodology lesson behind this comment.
    setLightboxUrl(null);
    if (location.hash === sentinel) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
    // Direct mode: photoUrls provided, skip fetch and render
    // immediately. Array.isArray guard tolerates malformed inputs.
    if (photoUrls != null) {
      const safeUrls = Array.isArray(photoUrls) ? photoUrls : [];
      setPhotos(safeUrls.map(url => ({ blob_url: url })));
      setLoading(false);
      setError(null);
      return;
    }
    if (!toolId) { setPhotos([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMedia(toolId, callerEmail)
      .then(data => {
        if (cancelled) return;
        setPhotos(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || 'Could not load photos.');
        setPhotos([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // photoUrls is in deps so direct-mode consumers re-render on
    // tool change; reference-stability contract documented above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, callerEmail, photoUrls]);

  /* ── Lightbox open / user-close (replace-only navigation) ────────── */
  const handleOpen = useCallback((url) => {
    setLightboxUrl(url);
    if (location.hash !== sentinel) {
      navigate(`${location.pathname}${location.search}${sentinel}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, sentinel, navigate]);

  const handleUserClose = useCallback(() => {
    setLightboxUrl(null);
    if (location.hash === sentinel) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, sentinel, navigate]);

  /* ── Escape closes lightbox while open ───────────────────────────── */
  useEffect(() => {
    if (!lightboxUrl) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') handleUserClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxUrl, handleUserClose]);

  /* ── Silent close when URL changes to one without the sentinel ─────
   * Watching location.key only — including lightboxUrl would close the
   * lightbox the moment it opens, since open is itself a state change
   * that re-runs the effect with a stale `location.key`. The lint hint
   * to "include everything you read" is wrong here; eslint-disable below
   * for that reason. */
  useEffect(() => {
    if (lightboxUrl && location.hash !== sentinel) {
      setLightboxUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /* ── Render ──────────────────────────────────────────────────────── */
  if (loading) {
    return <p className="pgp-status">Loading photos...</p>;
  }
  if (error) {
    return <p className="pgp-status pgp-status-error">Could not load photos.</p>;
  }
  if (photos.length === 0) {
    return <p className="pgp-status pgp-status-empty">No photos for this tool.</p>;
  }

  const lightboxOverlay = lightboxUrl && (
    <div className="pgp-lightbox" onClick={handleUserClose}>
      <button
        className="pgp-lightbox-close"
        onClick={handleUserClose}
        aria-label="Close"
      >
        ×
      </button>
      <img src={lightboxUrl} alt="" className="pgp-lightbox-img" />
    </div>
  );

  if (layout === 'grid') {
    return (
      <>
        <div className="pgp-grid">
          {photos.map((p, idx) => (
            <button
              key={p.id || p.media_id || `${idx}-${p.blob_url}`}
              type="button"
              className="pgp-thumb"
              onClick={() => handleOpen(p.blob_url)}
              aria-label={`Open photo ${idx + 1} in lightbox`}
            >
              <img
                src={p.blob_url}
                alt=""
                className="pgp-thumb-img"
                loading="lazy"
              />
            </button>
          ))}
        </div>
        {lightboxOverlay}
      </>
    );
  }

  const [main, ...rest] = photos;

  return (
    <>
      <div className="pgp-gallery">
        <button
          type="button"
          className="pgp-main"
          onClick={() => handleOpen(main.blob_url)}
          aria-label="Open photo 1 in lightbox"
        >
          <img
            src={main.blob_url}
            alt=""
            className="pgp-main-img"
            loading="lazy"
          />
        </button>

        {rest.length > 0 && (
          <div className="pgp-thumbs">
            {rest.map((p, idx) => (
              <button
                key={p.id || p.media_id || `${idx}-${p.blob_url}`}
                type="button"
                className="pgp-thumb"
                onClick={() => handleOpen(p.blob_url)}
                aria-label={`Open photo ${idx + 2} in lightbox`}
              >
                <img
                  src={p.blob_url}
                  alt=""
                  className="pgp-thumb-img"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {lightboxOverlay}
    </>
  );
}
